/**
 * The one sanctioned way to pass a dynamic value from TSX to CSS: set a CSS custom property on
 * an element and read it in the module CSS. TSX never writes style values (ESLint forbids the
 * `style` prop and `element.style` everywhere else).
 *
 *   const barRef = useCssVars<HTMLDivElement>({ "--bar-size": `${percent}%` });
 *   <div ref={barRef} className={styles.bar} />
 *   .bar { inline-size: var(--bar-size, 0%); }
 *
 * Prefer a `data-*` attribute when the value comes from a small fixed set (a status, a family id).
 */
import { useCallback, type RefCallback } from "react";

export type CssCustomProperty = `--${string}`;
export type CssVarValues = Partial<Record<CssCustomProperty, string | number | null | undefined>>;

/** Sets (or, for null/undefined/"", removes) CSS custom properties on an element. */
export function setCssVars(element: HTMLElement | SVGElement | null | undefined, values: CssVarValues): void {
  if (!element) return;
  for (const [name, value] of Object.entries(values)) {
    if (!name.startsWith("--")) throw new Error(`setCssVars only sets custom properties, got "${name}"`);
    if (value === null || value === undefined || value === "") element.style.removeProperty(name);
    else element.style.setProperty(name, String(value));
  }
}

/** A ref callback that keeps the element's custom properties in sync with `values`. */
export function useCssVars<T extends HTMLElement | SVGElement>(values: CssVarValues): RefCallback<T> {
  const serialized = JSON.stringify(values);
  return useCallback(
    (element: T | null) => {
      setCssVars(element, JSON.parse(serialized) as CssVarValues);
    },
    [serialized],
  );
}
